/**
 * WebAddressesPageViewModel - ViewModel for the My Addresses page
 * Provides badged address list, edit sheet state, and confirm logic with delivery validation
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import {
  useDeliveryStore,
  useOrderStore,
  useCartStore,
  useRestaurantStore,
  useAuthStore,
  DeliveryService,
  PlacesService,
  resolveDeliveryFee,
  resolveRemainingFees,
} from '@restaurant/core'
import type { DeliveryAddress, AddressResult } from '@restaurant/core'
import { useAddressViewModel } from '../sheets/WebAddressViewModel'
import { mapFormToDropOff } from '@restaurant/viewmodels/sheets/AddressViewModel'
import { apiClient } from '@restaurant/api-client'
import { safeGetLogger } from '@restaurant/logging'

export function useWebAddressesPageViewModel() {
  const { currentAddress, savedAddresses, setCurrentAddress, updateSavedAddress, addSavedAddress } = useDeliveryStore()
  const { orderType, setOrderType } = useOrderStore()
  const storeLocation = useRestaurantStore((s) => s.currentLocation)
  const currentRestaurant = useRestaurantStore((s) => s.currentRestaurant)
  const user = useAuthStore((s) => s.user)
  const locations = useRestaurantStore((s) => s.locations)
  const updateAddressFees = useDeliveryStore.getState().updateAddressFees

  const addressViewModel = useAddressViewModel()
  const deliveryService = useMemo(() => new DeliveryService({ apiClient }), [])
  const placesService = useMemo(() => new PlacesService({ apiClient }), [])

  // Sheet state
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false)
  const [editingAddress, setEditingAddress] = useState<DeliveryAddress | null>(null)
  const [isNewAddress, setIsNewAddress] = useState(false)

  // Alert state
  const [alertVisible, setAlertVisible] = useState(false)
  const [alertLocationName, setAlertLocationName] = useState('')
  const pendingConfirmedAddressRef = useRef<DeliveryAddress | null>(null)

  // Fee resolution state
  const [isCheckingFees, setIsCheckingFees] = useState(false)
  const phoneNumber = storeLocation?.phone || user?.phoneNumber || ''

  // Static map state
  const [staticMapUrl, setStaticMapUrl] = useState<string | null>(null)
  const [staticMapLoading, setStaticMapLoading] = useState(false)
  const [staticMapError, setStaticMapError] = useState(false)

  // Badged address list — current address first with badge, sorted by lastUsed
  const badgedAddresses = useMemo((): AddressResult[] => {
    if (savedAddresses.length === 0) return []

    const currentId = currentAddress?.id
    return savedAddresses
      .slice()
      .sort((a, b) => {
        if (a.id === currentId) return -1
        if (b.id === currentId) return 1
        return new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime()
      })
      .map((addr) => ({
        id: addr.id,
        addressLine1: addr.street,
        addressLine2: `${addr.city}, ${addr.state} ${addr.zipCode}`,
        ...(addr.id === currentId ? { badge: { variant: 'Default' as const, text: 'Current' } } : {}),
      }))
  }, [savedAddresses, currentAddress?.id])

  // Static map effect — fetch when editing address changes
  const selectedLat = editingAddress?.latitude
  const selectedLng = editingAddress?.longitude

  useEffect(() => {
    if (selectedLat == null || selectedLng == null) {
      setStaticMapUrl(null)
      setStaticMapError(false)
      return
    }

    setStaticMapLoading(true)
    setStaticMapError(false)
    let cancelled = false

    placesService.buildStaticMapUrl({
      latitude: selectedLat,
      longitude: selectedLng,
      zoom: 18,
      size: '750x400',
    })
      .then((url) => {
        if (!cancelled) {
          setStaticMapUrl(url)
          setStaticMapLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStaticMapError(true)
          setStaticMapLoading(false)
        }
      })

    return () => { cancelled = true }
  }, [selectedLat, selectedLng, placesService])

  // Static map component
  const staticMapComponent = useMemo(() => {
    if (selectedLat == null || selectedLng == null) return undefined

    if (staticMapLoading) {
      return (
        <div className="relative w-full bg-gray-100 rounded-xl border border-gray-200 flex items-center justify-center" style={{ aspectRatio: '750/400' }}>
          <div className="animate-spin h-6 w-6 border-2 border-gray-400 border-t-transparent rounded-full" />
        </div>
      )
    }

    if (staticMapError || !staticMapUrl) {
      return (
        <div className="relative w-full bg-gray-100 rounded-xl border border-gray-200 flex items-center justify-center text-gray-400 text-sm" style={{ aspectRatio: '750/400' }}>
          Map unavailable
        </div>
      )
    }

    return (
      <img
        src={staticMapUrl}
        alt="Delivery location map"
        className="w-full rounded-xl border border-gray-200 object-cover"
        style={{ aspectRatio: '750/400' }}
      />
    )
  }, [selectedLat, selectedLng, staticMapLoading, staticMapError, staticMapUrl])

  // Search state — delegates to addressViewModel's built-in search
  const searchQuery = addressViewModel.searchQuery
  const searchResults = addressViewModel.searchResults

  const handleSearchChange = useCallback((value: string) => {
    addressViewModel.setSearchQuery(value)
  }, [addressViewModel])

  // Handle search result selection — new address from autocomplete
  const handleSearchResultSelect = useCallback((addressResult: AddressResult) => {
    const fullAddress = addressViewModel.searchResults.find((r) => r.id === addressResult.id)
    if (!fullAddress) return

    addressViewModel.selectAddress(fullAddress)
    setIsNewAddress(true)

    // Build a temporary DeliveryAddress for static map (lat/lng)
    setEditingAddress({
      id: fullAddress.id,
      userId: user?.id || '',
      businessId: currentRestaurant?.id ?? '',
      street: fullAddress.street,
      city: fullAddress.city,
      state: fullAddress.state,
      zipCode: fullAddress.zipCode,
      latitude: fullAddress.latitude,
      longitude: fullAddress.longitude,
      lastUsed: new Date().toISOString(),
      storeDeliveryFees: {},
    })

    // Clear search and open sheet
    addressViewModel.setSearchQuery('')
    setIsEditSheetOpen(true)
  }, [addressViewModel, user, currentRestaurant])

  // Handle saved address row tap — open edit sheet
  const handleAddressSelect = useCallback((addressResult: AddressResult) => {
    const fullAddress = savedAddresses.find((a) => a.id === addressResult.id)
    if (!fullAddress) return

    setEditingAddress(fullAddress)
    setIsNewAddress(false)
    addressViewModel.prefillFromAddress(fullAddress)
    setIsEditSheetOpen(true)
  }, [savedAddresses, addressViewModel])

  // Handle confirm
  const handleConfirmAddress = useCallback(async () => {
    if (!editingAddress) return

    const selectedAddr = addressViewModel.selectedAddress
    if (!selectedAddr && isNewAddress) return

    try {
      const now = new Date().toISOString()
      let confirmedAddress: DeliveryAddress

      if (isNewAddress) {
        // New address from autocomplete — save to backend
        const newAddress: Omit<DeliveryAddress, 'id' | 'lastUsed'> = {
          userId: user?.id || '',
          businessId: currentRestaurant?.id ?? '',
          street: selectedAddr!.street,
          city: selectedAddr!.city,
          state: selectedAddr!.state,
          zipCode: selectedAddr!.zipCode,
          apartment: addressViewModel.addressDetails.unitNumber || undefined,
          entryCode: addressViewModel.addressDetails.entryCode || undefined,
          buildingName: addressViewModel.addressDetails.buildingName || undefined,
          latitude: selectedAddr!.latitude,
          longitude: selectedAddr!.longitude,
          deliveryDetails: {
            dropOffOption: mapFormToDropOff(addressViewModel.addressDetails.deliveryPreference),
            instructions: addressViewModel.addressDetails.deliveryNotes || undefined,
          },
          storeDeliveryFees: {},
        }
        const saved = await deliveryService.saveUserAddress(newAddress)
        addSavedAddress(saved)
        confirmedAddress = saved
      } else {
        // Existing address — check for changes
        const hasChanges = addressViewModel.hasDetailsChanged()

        if (hasChanges) {
          const updates = {
            apartment: addressViewModel.addressDetails.unitNumber || undefined,
            entryCode: addressViewModel.addressDetails.entryCode || undefined,
            buildingName: addressViewModel.addressDetails.buildingName || undefined,
            deliveryDetails: {
              dropOffOption: mapFormToDropOff(addressViewModel.addressDetails.deliveryPreference),
              instructions: addressViewModel.addressDetails.deliveryNotes || undefined,
            },
            lastUsed: now,
          }
          await deliveryService.updateUserAddress(editingAddress.id, updates)
          updateSavedAddress(editingAddress.id, updates)
          confirmedAddress = { ...editingAddress, ...updates }
        } else {
          // No changes — just update lastUsed and set as current
          await deliveryService.updateUserAddress(editingAddress.id, { lastUsed: now })
          updateSavedAddress(editingAddress.id, { lastUsed: now })
          confirmedAddress = { ...editingAddress, lastUsed: now }
        }
      }

      // Fee resolution — fetch delivery fee for current location
      if (orderType === 'Delivery' && storeLocation && currentRestaurant) {
        setIsCheckingFees(true)
        try {
          const feeResult = await resolveDeliveryFee(confirmedAddress, {
            deliveryService,
            businessId: currentRestaurant.id,
            currentLocation: storeLocation,
            locations,
            phoneNumber,
            updateAddressFees,
          })
          if (!feeResult) {
            // Fetch failed — keep sheet open for retry
            return
          }

          if (feeResult.fee >= 0) {
            setCurrentAddress(feeResult.addressWithFees)
            useCartStore.getState().setDeliveryFee(feeResult.fee)
          } else {
            // Out of range — show alert
            pendingConfirmedAddressRef.current = feeResult.addressWithFees
            setIsEditSheetOpen(false)
            setEditingAddress(null)
            setIsNewAddress(false)
            setAlertLocationName(storeLocation.name || 'this location')
            setAlertVisible(true)
            return
          }
        } catch {
          return
        } finally {
          setIsCheckingFees(false)
        }
      } else {
        setCurrentAddress(confirmedAddress)
      }

      // Background: pre-cache fees for all locations (both Delivery and Pickup)
      if (currentRestaurant && storeLocation) {
        const addressForBatch = orderType === 'Delivery'
          ? (useDeliveryStore.getState().currentAddress ?? confirmedAddress)
          : confirmedAddress
        resolveRemainingFees(addressForBatch, {
          deliveryService,
          businessId: currentRestaurant.id,
          currentLocation: storeLocation,
          locations,
          phoneNumber,
          updateAddressFees,
        }).catch(() => {})
      }

      // Close sheet
      setIsEditSheetOpen(false)
      setEditingAddress(null)
      setIsNewAddress(false)
    } catch (err) {
      safeGetLogger()?.error('Address save/update failed', {
        domain: 'delivery',
        error: err instanceof Error ? err.message : String(err),
      })
      // Sheet stays open for retry
    }
  }, [editingAddress, isNewAddress, addressViewModel, deliveryService, orderType, storeLocation, updateSavedAddress, setCurrentAddress, addSavedAddress, user, currentRestaurant, locations, phoneNumber, updateAddressFees])

  // Alert actions
  const handleSwitchToPickup = useCallback(() => {
    // Set the address as current and switch to Pickup
    const pending = pendingConfirmedAddressRef.current
    if (pending) {
      setCurrentAddress(pending)
      pendingConfirmedAddressRef.current = null
    }
    setOrderType('Pickup')
    useCartStore.getState().setDeliveryFee(0)
    setAlertVisible(false)
  }, [setCurrentAddress, setOrderType])

  const handleAlertChangeAddress = useCallback(() => {
    // Don't set the address — let user pick a different one from the list
    // Stay in Delivery mode
    pendingConfirmedAddressRef.current = null
    setAlertVisible(false)
  }, [])

  // Close sheet
  const handleCloseSheet = useCallback(() => {
    setIsEditSheetOpen(false)
    setEditingAddress(null)
    setIsNewAddress(false)
    addressViewModel.goBackToSearch()
  }, [addressViewModel])

  // Alert props
  const alertProps = useMemo(() => ({
    visible: alertVisible,
    title: 'Address Too Far',
    description: `Delivery is not available from ${alertLocationName} to this address.`,
    primaryAction: {
      text: 'Switch to Pickup',
      onPress: handleSwitchToPickup,
    },
    secondaryAction: {
      text: 'Change Address',
      onPress: handleAlertChangeAddress,
    },
  }), [alertVisible, alertLocationName, handleSwitchToPickup, handleAlertChangeAddress])

  return {
    badgedAddresses,
    isEditSheetOpen,
    isCheckingFees,
    searchQuery,
    searchResults,
    handleSearchChange,
    handleSearchResultSelect,
    handleAddressSelect,
    handleConfirmAddress,
    handleCloseSheet,
    addressViewModel,
    staticMapComponent,
    alertProps,
  }
}
